import React, {useContext, useEffect, useState, useRef} from "react";
import ListItem, {ICON_TYPES} from "../common/ListItem";
import {TemplateContext} from "../common/TemplateContext";
import useCurrentShortcut from "../hooks/useCurrentShortcut";
import {UserContext} from "../common/UserContext";
import {BrandContext} from "../common/BrandContext";
import NavAccessibilityWrap from "./NavAccessibilityWrap";
import ListGroup from "../common/ListGroup";
import {NavigationContext} from "../common/NavigationContext";
import {useScreenMode} from "../common/context/ScreenModeContext";

const ContextualNavigationDevelopment = props => {
    const {contextualShortcuts, compressed} = props;
    const menuRef = useRef();
    const [isOverflowing, setOverflowing] = useState(false);
    const {smallScreenMode} = useScreenMode();

    useEffect(() => {
        const listItem = menuRef.current.getElementsByClassName('ch-list-item')[0];
        let shortcutsTotal = 0;
        if (listItem) {
            contextualShortcuts.map(shortcut => {
                if (shortcut.childShortcuts.length > 0) {
                    shortcutsTotal += shortcut.childShortcuts.length;
                } else {
                    shortcutsTotal++;
                }
            });
        }

        const resizeListener = (e) => {
            if (listItem) {
                const menu = menuRef.current.closest('.ch-nav-contextual-body');
                const menuInnerHeight= shortcutsTotal * listItem.getBoundingClientRect().height;
                setOverflowing(menu.clientHeight < menuInnerHeight);
            }
        };
        window.addEventListener('resize', resizeListener);
        window.dispatchEvent(new Event('resize'));
        return () => {
            window.removeEventListener('resize', resizeListener);
        }
    });

    return (
        <>
            {!smallScreenMode &&
                <div className={'ch-contextual-nav-accessible-skip'}>
                    <a href="#nav-menu-compress-button" tabIndex={0}>Skip navigation block</a>
                </div>
            }
            <NavAccessibilityWrap enableTabNav={true} aria-label="Main Navigation">
                <ul ref={menuRef} className={'ch-list ch-nav-shortcuts ch-nav-group'}>
                    {contextualShortcuts.map((contextShortcut, index) => {
                        if (contextShortcut.childShortcuts.length > 0) {
                            return (
                                <ContextualGroupItem key={index} title={contextShortcut.name} disableGrouping={!isOverflowing} startOpen={false}>
                                    {contextShortcut.childShortcuts.map((childContextShortcut, subIndex) => (
                                        <ContextualListItem key={subIndex} tabIndex={subIndex == 0 ? 0 : -1} contextShortcut={childContextShortcut} compressed={compressed} />
                                    ))}
                                </ContextualGroupItem>
                            )
                        }

                        return (
                            <ContextualListItem key={index} tabIndex={index == 0 ? 0 : -1} contextShortcut={contextShortcut} compressed={compressed} />
                        )
                    })}
                </ul>
            </NavAccessibilityWrap>
        </>
    )
}

const ContextualListItem = props => {
    const {contextShortcut, compressed, tabIndex = 0} = props;

    const {singlePage, shortcut} = useContext(TemplateContext);
    const {user} = useContext(UserContext);
    const brandConfig = useContext(BrandContext);
    const {loadShortcut} = useCurrentShortcut();
    const { setNavVisible, setDrawerVisible } = useContext(NavigationContext);

    const handleClick = (shortcut) => {
        loadShortcut(shortcut);
        setNavVisible(false);

        const mainEl = document.querySelector('main');
        if (mainEl) {
            mainEl.focus();
        }
    }

    let alertText;
    if(contextShortcut.highlightText != undefined && contextShortcut.highlightText !== ''){
        alertText = contextShortcut.highlightText;
    }else if(contextShortcut.disabled && contextShortcut.disabledDescription != undefined && contextShortcut.disabledDescription !== ''){
        alertText = contextShortcut.disabledDescription;
    }

    if ([43,17,85,101,161].includes(contextShortcut.shortcutId)) {
        return null;
    }
    if ([155,163].includes(contextShortcut.shortcutId) && !brandConfig.hasDiagnosticTests) {
        return null;
    }
    if (['IGCSE','OCR'].includes(brandConfig.brandCode) && contextShortcut.shortcutId == 36) {
        return null;
    }
    if (user.supplementary && contextShortcut.shortcutId === 125) {
        return null;
    }
    if (!user.supplementary && contextShortcut.shortcutId === 152) {
        return null;
    }
    if (user.adminUser && contextShortcut.shortcutId === 71) {
        return null;
    }

    // Temporary left nav control for My Course and Search Resources. To be removed after full rollout.
    if (brandConfig.useDevelopmentNavigation && contextShortcut.shortcutId === 2 || contextShortcut.shortcutId === 36){
        return null;
    }

    // Data attribute for hotjar
    const gtmName = contextShortcut.name.toLowerCase().replace(/ /g, '-');
    const gtmHighlight = contextShortcut.highlightText ? `-${contextShortcut.highlightText.toLowerCase()}` : '';
    const gtmValue = `sidebar-menu-${gtmName}${gtmHighlight}`;

    return (
        <ListItem
            isDisclosureItem={true}
            className="ch-contextual-list-item"
            tabkey={contextShortcut.shortcutId}
            tabIndex={tabIndex}
            alertText={alertText}
            iconOnly={compressed}
            disabled={(contextShortcut.shortcutId == 1 && !user.conceptId) || (contextShortcut.schoolRequired && (!user.schoolId && !user.actForSchoolId)) || contextShortcut.disabled}
            active={shortcut && shortcut.shortcutId === contextShortcut.shortcutId}
            highlight={contextShortcut.highlight}
            icon={contextShortcut.iconClass}
            iconType={ICON_TYPES.INTERNAL_SVG}
            iconSize={[40, 40]}
            iconBg={true}
            tooltip={contextShortcut.name}
            onClick={() => {handleClick(contextShortcut)}}
            data-gtm-value={gtmValue}
        >
            {contextShortcut.name}
            {contextShortcut.highlightText != undefined && contextShortcut.highlightText != '' && (
                <span className={`ch-list-item-highlight-text${contextShortcut.highlightText === 'Legacy' ? ' legacy-label' : ''}`}>
                    {contextShortcut.highlightText}
                </span>
            )}
        </ListItem>
    )
}

const ContextualGroupItem = props => {
    const {disableGrouping, title, startOpen = true, children} = props;
    const [isOpen, setIsOpen] = useState(startOpen);

    if (disableGrouping) {
        return (
            <>{children}</>
        )
    }
    return (
        <ListGroup title={title} startOpen={startOpen}>
            {children}
        </ListGroup>
    )
}

export default ContextualNavigationDevelopment;